<template>
  <div
    class="job-profile-comparison-table"
  >
    <PhantomScrollWrapper
      class="table-scroll-wrapper"
      @dragstart="setRowDragImage"
    >
      <table class="comparison-table">
        <thead>
          <draggable
            tag="tr"
            :list="headerColumnList"
            item-key="id"
            :move="forbidFieldMove"
            ghost-class="ghost"
            handle=".col-drag-handle"
            :bubble-scroll="true"
            :scroll-sensitivity="80"
            :scroll-speed="12"
            :force-auto-scroll-fallback="true"
            @end="onHeaderDragEnd"
          >
            <template #item="{ element }">
              <th
                class="col-profile"
                :class="{
                  'sticky-col': element.id === 'fieldLabel',
                  'col-drag-handle': element.id !== 'fieldLabel',
                  'col-profile-header-rich': element.id !== 'fieldLabel',
                }"
              >
                <template v-if="element.id === 'fieldLabel'">
                  <span aria-hidden="true" />
                </template>
                <template v-else>
                  <ComparisonProfileHeader
                    :header="element.header"
                    :job-code="element.jobCode"
                    :job-level="element.jobLevel"
                    :job-family-names="element.jobFamilyNames"
                    :employee-count-display="element.employeeCountDisplay"
                    :profile-id="element.id"
                    :alert-codes="element.alertCodes"
                    :tooltip-context="element.tooltipContext"
                    @open-sidebar="openCommentSidebarForProfile(element.id, element.header)"
                    @remove="emit('remove-profile', element.id)"
                  />
                </template>
              </th>
            </template>
          </draggable>
        </thead>
        <draggable
          v-model="orderedRowsData"
          tag="tbody"
          item-key="fieldId"
          ghost-class="ghost"
          handle=".row-drag-handle"
          :bubble-scroll="true"
          :scroll-sensitivity="40"
          :scroll-speed="12"
          :force-auto-scroll-fallback="true"
          @end="onRowDragEnd"
        >
          <template #item="{ element }">
            <tr>
              <td
                class="col-profile cell-td row-drag-handle sticky-col"
              >
                <span class="cell-text">
                  <v-icon
                    size="18"
                    icon="material-symbols:drag-indicator"
                    color="textLighter"
                  />
                  {{ formatCellValue(toPlainText(element.fieldLabel)) }}
                </span>
                <button
                  type="button"
                  class="cell-remove-row-btn"
                  aria-label="Remove row"
                  @click.stop="toggleFieldVisibility(element.fieldId, false)"
                >
                  <v-icon
                    size="14"
                    icon="mdi-close"
                  />
                </button>
              </td>
              <td
                v-for="colId in profileColumnOrder"
                :key="colId"
                class="col-profile cell-td"
              >
                <span class="cell-text">
                  {{ formatCellValue(toPlainText(element[colId])) }}
                </span>
                <button
                  type="button"
                  class="cell-comment-btn"
                  aria-label="Add comment"
                  @click="openCommentModalForRow(element, colId)"
                >
                  <DpplSvg file-name="icons/Icon_Comment_Plus" />
                </button>
              </td>
            </tr>
          </template>
        </draggable>
      </table>
    </PhantomScrollWrapper>

    <p v-if="!orderedRowsData.length" class="text-medium-emphasis pa-4">
      No fields to compare. Select which rows (fields) to show above.
    </p>

    <TableCellCommentModal
      :show="!!commentModalContext"
      :profile-ulid="commentModalContext?.profileUlid || ''"
      :profile-title="commentModalContext?.profileTitle || ''"
      :field-label="commentModalContext?.fieldLabel || ''"
      :quoted-text="commentModalContext?.quotedText || ''"
      :thread-url="commentModalContext?.threadUrl || ''"
      @close="commentModalContext = null"
      @comment-added="handleCommentAdded"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useRoute, useRouter } from 'vue-router';
import {
  useVueTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/vue-table';
import draggable from 'vuedraggable';
import { arrayToCSVDownload } from '@/helpers/fileDownload';
import { useCommenting } from '@/stores/commenting/commenting';
import { useTracking } from '@/stores/tracking/tracking';
import { useColors } from '@/composables/colors';
import PhantomScrollWrapper from '@/components/shared/PhantomScrollWrapper.vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import ComparisonProfileHeader from './ComparisonProfileHeader.vue';
import TableCellCommentModal from './TableCellCommentModal.vue';
import {
  sortProfileIdsByJobLevel,
  buildAllFieldsFromProfiles,
  buildTableRowsFromFieldsAndProfiles,
  toPlainText,
  formatCellValue,
  buildCsvRowsForComparisonTable,
} from './helpers';
import {
  computeArchitectureAlerts,
  getProfileAlertTooltipContext,
  sortArchitectureAlertCodes,
} from './alertHelpers';

const formatEmployeeCountLabel = (count) => {
  const number = Number(count) || 0;

  return `${number} ${number === 1 ? 'employee' : 'employees'}`;
};

/**
 * This table compares the currently selected job family's profiles side by side:
 * each row is a field (e.g. Job Description), each column is a profile. The parent
 * receives column/row visibility controls via update:controls so it can drive the
 * Columns and Rows filter dropdowns.
 */

const props = defineProps({
  profiles: {
    type: Array,
    default: () => [],
  },
  fieldKeysOrder: {
    type: Array,
    default: () => [],
  },
  hiddenFieldKeys: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:controls', 'remove-profile']);

const route = useRoute();
const router = useRouter();

const { white, grey400, textDarkest } = useColors();

const { treeData } = storeToRefs(useJobArchitecture());

const architectureAlerts = computed(() => {
  if (!treeData.value) return null;

  return computeArchitectureAlerts(treeData.value);
});

const getProfileHeaderAlertData = (ulid) => {
  const bundle = architectureAlerts.value;

  if (!bundle) {
    return {
      alertCodes: [],
      tooltipContext: { familyNames: [], groupNames: [] },
    };
  }

  return {
    alertCodes: sortArchitectureAlertCodes(bundle.byProfile.get(ulid) || []),
    tooltipContext: getProfileAlertTooltipContext(bundle.profileCounts, ulid),
  };
};

const columnVisibilityState = ref({});
const visibleFieldIds = ref(new Set());
const commentModalContext = ref(null);

// Always ['fieldLabel', ...profileUlids]. Drives table column order; profile columns are draggable.
const columnOrder = ref([]);
// Field ids in display order. Subset of visible fields; user can reorder via drag.
const visibleFieldOrder = ref([]);
// Header row list for vuedraggable: [{ id, header }, ...]. Synced from columnOrder; on drag end sync back.
const headerColumnList = ref([]);

const profiles = computed(() => props.profiles || []);

const profileColumns = computed(() => profiles.value.map((profile) => ({
  ulid: profile.ulid,
  title: profile.title || profile.hris_job_code || profile.ulid,
  jobCode: profile.hris_job_code || '',
  jobLevel: profile.job_level || '',
  employeeCount: profile.employee_count ?? 0,
  jobFamilyNames: (profile.job_families || []).map((family) => family.name).join(', '),
})));

// Union of all field ids across profiles (fields + custom_fields). Sorted by props.fieldKeysOrder, else FIELD_ROW_ORDER, then label.
const allFields = computed(() => buildAllFieldsFromProfiles(
  profiles.value,
  props.fieldKeysOrder || [],
  props.hiddenFieldKeys || [],
));

// One row per field; each column (profile ulid) holds that profile's value.
const tableRows = computed(() => buildTableRowsFromFieldsAndProfiles(allFields.value, profiles.value));

// Row data in display order (driven by visibleFieldOrder)
const visibleRowsData = computed(() => {
  const order = visibleFieldOrder.value;
  const rows = tableRows.value;

  if (order.length === 0) return [];
  return order.map((fieldId) => rows.find((r) => r.fieldId === fieldId)).filter(Boolean);
});

const columnHelper = createColumnHelper();

// Field label column (sticky) plus one accessor per profile. First column = field name; rest from profileColumns.
const columns = computed(() => {
  const columnDefs = [
    columnHelper.accessor('fieldLabel', {
      id: 'fieldLabel',
      header: '',
      enableHiding: false,
    }),
  ];

  const profileColumnList = profileColumns.value;

  profileColumnList.forEach((profileColumn) => {
    columnDefs.push(
      columnHelper.accessor((row) => row[profileColumn.ulid], {
        id: profileColumn.ulid,
        header: profileColumn.title || profileColumn.ulid,
        enableHiding: true,
      }),
    );
  });

  return columnDefs;
});

// visibleRowsData = rows user chose via Rows filter. Table needs getter for columns to avoid ref unwrap issues.
const table = useVueTable({
  data: visibleRowsData,
  get columns() {
    return columns.value || [];
  },
  getCoreRowModel: getCoreRowModel(),
  getRowId: (row) => row.fieldId,
  state: {
    get columnVisibility() {
      return columnVisibilityState.value;
    },
    get columnOrder() {
      return columnOrder.value;
    },
  },
  onColumnVisibilityChange: (updater) => {
    const updatedVisibility = typeof updater === 'function' ? updater(columnVisibilityState.value) : updater;

    columnVisibilityState.value = updatedVisibility;
  },
  onColumnOrderChange: (updater) => {
    const updatedOrder = typeof updater === 'function' ? updater(columnOrder.value) : updater;
    const profileIds = profileColumns.value.map((p) => p.ulid);
    const normalized = updatedOrder.filter(
      (id) => id === 'fieldLabel' || profileIds.includes(id),
    );

    columnOrder.value = ['fieldLabel', ...normalized.filter((id) => id !== 'fieldLabel')];
  },
});

const commenting = useCommenting();

const profileEditorUrl = (ulid) => {
  const resolved = router.resolve({
    name: 'job-profile-hris-view',
    params: { id: ulid },
  });

  return `${window.location.origin}${resolved.href}`;
};

const openCommentModalForRow = async (rowData, profileUlid) => {
  await commenting.loadCord();

  const profileColumn = profileColumns.value.find((profile) => profile.ulid === profileUlid);

  commentModalContext.value = {
    profileUlid,
    profileTitle: profileColumn?.title || profileUlid,
    fieldLabel: rowData?.fieldLabel || '',
    quotedText: toPlainText(rowData?.[profileUlid]) || '',
    threadUrl: profileEditorUrl(profileUlid),
  };
};

const openCommentSidebarForProfile = (profileUlid, profileTitle) => {
  useTracking().jaMatrixOpenedCommentSidebar();

  router.push({
    query: {
      ...route.query,
      preview: profileUlid,
      type: 'jobProfileComments',
      previewTitle: profileTitle,
    },
  });
};

const handleCommentAdded = ({ section }) => {
  useTracking().jaMatrixAddedComment({ section });
};

const visibleFieldIdsArray = computed(() => Array.from(visibleFieldIds.value));

const setAllColumnsVisible = (visible) => {
  const updatedVisibility = { ...columnVisibilityState.value };

  profileColumns.value.forEach((profileColumn) => {
    updatedVisibility[profileColumn.ulid] = visible;
  });

  columnVisibilityState.value = updatedVisibility;

  profileColumns.value.forEach((profileColumn) => {
    table.getColumn(profileColumn.ulid)?.toggleVisibility(visible);
  });
};

const toggleColumnVisibility = (columnId, visible) => {
  useTracking().jaMatrixToggledColumnVisibility();

  columnVisibilityState.value = { ...columnVisibilityState.value, [columnId]: visible };
  const column = table.getColumn(columnId);

  if (column?.toggleVisibility) {
    column.toggleVisibility(visible);
  }
};

const toggleFieldVisibility = (fieldId, visible) => {
  if (!visible) {
    useTracking().jaMatrixRemovedJobProfileField();
    useTracking().jaMatrixToggledRowVisibility();
  }

  const updatedVisibleIds = new Set(visibleFieldIds.value);

  if (visible) {
    updatedVisibleIds.add(fieldId);

    if (!visibleFieldOrder.value.includes(fieldId)) {
      visibleFieldOrder.value = [fieldId, ...visibleFieldOrder.value];
    }
  } else {
    updatedVisibleIds.delete(fieldId);
    visibleFieldOrder.value = visibleFieldOrder.value.filter((id) => id !== fieldId);
  }

  visibleFieldIds.value = updatedVisibleIds;
};

// Seed visible rows once on mount; settings and profiles are ready before the table mounts.
onMounted(() => {
  const ids = allFields.value.map((field) => field.id);

  visibleFieldIds.value = new Set(ids);
  visibleFieldOrder.value = [...ids];
});

const syncColumnVisibilityForProfiles = (profileColumnList) => {
  const updatedVisibility = { ...columnVisibilityState.value };
  let hasNewColumns = false;

  profileColumnList.forEach((profileColumn) => {
    if (updatedVisibility[profileColumn.ulid] === undefined) {
      updatedVisibility[profileColumn.ulid] = true;
      hasNewColumns = true;
    }
  });

  if (hasNewColumns) columnVisibilityState.value = updatedVisibility;
};

const syncColumnOrderForProfiles = (profileColumnList) => {
  const currentOrder = columnOrder.value;
  const profileIds = (profileColumnList || []).map((p) => p.ulid);
  const existingProfileOrder = currentOrder.filter(
    (id) => id !== 'fieldLabel' && profileIds.includes(id),
  );
  const newProfileIds = profileIds.filter((id) => !existingProfileOrder.includes(id));
  const sortedNew = sortProfileIdsByJobLevel(newProfileIds, profiles.value);
  const orderedProfileIds = existingProfileOrder.length > 0
    ? [...sortedNew, ...existingProfileOrder]
    : sortProfileIdsByJobLevel(profileIds, profiles.value);

  columnOrder.value = ['fieldLabel', ...orderedProfileIds];
};

watch(
  profileColumns,
  (profileColumnList) => {
    syncColumnVisibilityForProfiles(profileColumnList);
    syncColumnOrderForProfiles(profileColumnList);
  },
  { immediate: true },
);

// Sync headerColumnList from columnOrder, excluding hidden columns (so table shows only visible columns).
watch(
  [columnOrder, profileColumns, () => columnVisibilityState.value, treeData],
  () => {
    const getHeader = (id) => {
      if (id === 'fieldLabel') return '';

      const profileColumn = profileColumns.value.find((x) => x.ulid === id);

      return profileColumn?.title || id;
    };

    const visibleIds = columnOrder.value.filter(
      (id) => id === 'fieldLabel' || columnVisibilityState.value[id] !== false,
    );

    headerColumnList.value = visibleIds.map((id) => {
      if (id === 'fieldLabel') {
        return { id, header: '' };
      }

      const profileColumn = profileColumns.value.find((x) => x.ulid === id);
      const { alertCodes, tooltipContext } = getProfileHeaderAlertData(id);

      return {
        id,
        header: getHeader(id),
        jobCode: profileColumn?.jobCode || '',
        jobLevel: profileColumn?.jobLevel || '',
        employeeCountDisplay: formatEmployeeCountLabel(profileColumn?.employeeCount),
        jobFamilyNames: profileColumn?.jobFamilyNames || '',
        alertCodes,
        tooltipContext,
      };
    });
  },
  { immediate: true },
);

const onHeaderDragEnd = (event) => {
  if (event?.oldIndex !== event?.newIndex) {
    useTracking().jaMatrixReorderedColumn();
  }

  const newOrder = headerColumnList.value.map((item) => item.id);
  const hiddenIds = columnOrder.value.filter(
    (id) => id !== 'fieldLabel' && !newOrder.includes(id),
  );

  columnOrder.value = [...newOrder, ...hiddenIds];
};

const onRowDragEnd = (event) => {
  if (event?.oldIndex !== event?.newIndex) {
    useTracking().jaMatrixReorderedRow();
  }
};

// Custom drag image for row. It shows the first cell's text instead of the whole row.
const setRowDragImage = (event) => {
  const tr = event.target?.closest?.('tr');

  if (!tr || !tr.closest('.comparison-table tbody') || !event.dataTransfer) return;

  const firstCell = tr.querySelector('td:first-child');

  if (!firstCell) return;

  const dragImage = document.createElement('div');

  dragImage.textContent = (firstCell.textContent || '').trim() || '—';

  Object.assign(dragImage.style, {
    padding: '8px 0px 8px 16px',
    minWidth: '220px',
    maxWidth: '375px',
    background: white.value,
    border: `1px solid ${grey400.value}`,
    borderRadius: '4px',
    fontSize: '12px',
    lineHeight: '16px',
    color: textDarkest.value,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    opacity: '0.9',
    position: 'absolute',
    top: '-9999px',
    left: '0',
  });

  document.body.appendChild(dragImage);
  event.dataTransfer.setDragImage(dragImage, 0, 0);

  requestAnimationFrame(() => dragImage.remove());
};

// Prevent dragging the Field column and prevent dropping any column into the first slot
const forbidFieldMove = (evt) => {
  if (evt.draggedContext?.element?.id === 'fieldLabel') return false;
  if (evt.draggedContext?.futureIndex === 0) return false;

  return true;
};

// Writable computed for tbody v-model: order comes from visibleFieldOrder; setter updates visibleFieldOrder.
const orderedRowsData = computed({
  get() {
    return visibleFieldOrder.value
      .map((fieldId) => tableRows.value.find((row) => row.fieldId === fieldId))
      .filter(Boolean);
  },
  set(newRows) {
    visibleFieldOrder.value = (newRows || []).map((row) => row.fieldId).filter(Boolean);
  },
});

// Profile column ids in display order, excluding hidden (so tbody only renders visible columns).
const profileColumnOrder = computed(() => columnOrder.value.filter(
  (id) => id !== 'fieldLabel' && columnVisibilityState.value[id] !== false,
));

// Profile columns in table order (for Columns dropdown).
const orderedProfileColumns = computed(() => columnOrder.value
  .filter((id) => id !== 'fieldLabel')
  .map((id) => profileColumns.value.find((p) => p.ulid === id))
  .filter(Boolean));

// Fields in table order for Rows dropdown: visible first (in visibleFieldOrder), then hidden.
const orderedFieldsForRows = computed(() => {
  const visible = visibleFieldOrder.value
    .map((id) => allFields.value.find((field) => field.id === id))
    .filter(Boolean);
  const hidden = allFields.value.filter((field) => !visibleFieldIds.value.has(field.id));

  return [...visible, ...hidden];
});

const hasExportableResults = computed(() => (
  profiles.value.length > 0
  && profileColumnOrder.value.length > 0
  && orderedRowsData.value.length > 0
));

const downloadCsv = (options = {}) => {
  if (!hasExportableResults.value) return;

  const { fileName } = options;
  const headers = headerColumnList.value;
  const array = buildCsvRowsForComparisonTable(headers, orderedRowsData.value, toPlainText);

  if (!array.length) return;

  arrayToCSVDownload({
    array,
    title: fileName || 'job-family-profiles.csv',
    autoClick: true,
  });
};

// Send column/row options and toggle handlers to parent for Columns and Rows filter dropdowns.
watch(
  [
    orderedProfileColumns,
    () => columnVisibilityState.value,
    allFields,
    orderedFieldsForRows,
    visibleFieldIdsArray,
    hasExportableResults,
  ],
  () => {
    emit('update:controls', {
      profileColumns: orderedProfileColumns.value,
      columnVisibilityState: columnVisibilityState.value,
      allFields: allFields.value,
      orderedFieldsForRows: orderedFieldsForRows.value,
      visibleFieldIdsArray: visibleFieldIdsArray.value,
      hasExportableResults: hasExportableResults.value,
      toggleColumnVisibility,
      toggleFieldVisibility,
      setAllColumnsVisible,
      downloadCsv,
    });
  },
  { immediate: true, deep: true },
);
</script>

<style scoped lang="less">
.job-profile-comparison-table {
  display: flex;
  flex-direction: column;
}

.table-scroll-wrapper :deep(.phantom-scroll-target) {
  border: 1px solid @stroke-default;
  border-radius: 4px;
  background: @grey-100;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  color: @text-darkest;
}

.comparison-table th,
.comparison-table td {
  padding: 8px 16px;
  text-align: left;
  border-bottom: 1px solid @stroke-default;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 375px;
  vertical-align: top;
}

.comparison-table th.sticky-col,
.comparison-table td.sticky-col {
  max-width: 220px;
  padding-right: 0;
}

.comparison-table th {
  background: @grey-100;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: @text-light;
  position: sticky;
  top: 0;
  z-index: 1;
}

.comparison-table th:hover :deep(.cell-comment-btn),
.comparison-table th.col-profile-header-rich:focus-within :deep(.cell-comment-btn) {
  opacity: 1;
}

/* Field header (corner cell) sticks on both scroll axes and stays above other sticky cells */
.comparison-table th.sticky-col {
  left: 0;
  top: 0;
  z-index: 3;
  background: @grey-100;
}

.comparison-table tbody tr {
  background: @white;
}

.comparison-table tbody tr:last-child td {
  border-bottom: none;
}

.comparison-table tbody tr:hover {
  background: @white;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: @grey-100;
  z-index: 2;
  box-sizing: border-box;
  min-width: 220px;
  width: 220px;
  max-width: 220px;
}

.comparison-table tbody td.sticky-col {
  position: sticky;
  left: 0;
  background: @white;
  padding-right: 28px;
}

.comparison-table tbody tr:hover td.sticky-col {
  background: @white;
}

.col-profile {
  min-width: 375px;
}

.comparison-table th.col-profile.sticky-col,
.comparison-table td.col-profile.sticky-col {
  min-width: 220px;
}

.cell-td {
  position: relative;
}

.comparison-table tbody td.cell-td:not(.sticky-col) {
  padding-right: 24px;
}

.cell-text {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: pre-line;
}

.comparison-table :deep(.cell-comment-btn) {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 18px;
  height: 18px;
  background: none;
  border: none;
  color: @text-light;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.comparison-table td.cell-td:hover :deep(.cell-comment-btn) {
  opacity: 1;
}

.comparison-table :deep(.cell-comment-btn:hover) {
  opacity: 1;
}

.comparison-table :deep(.cell-comment-btn svg) {
  width: 14px;
  height: 14px;
}

.comparison-table :deep(.cell-remove-row-btn) {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: transparent;
  color: @text-light;
  cursor: pointer;
  border-radius: 4px;
}

.comparison-table :deep(.cell-remove-row-btn:hover) {
  color: @text-darkest;
  background: fade(@grey-900, 6%);
}

.comparison-table :deep(.cell-remove-row-btn svg) {
  width: 14px;
  height: 14px;
}

/* Drag ghost for vuedraggable column/row reorder */
.comparison-table :deep(.ghost) {
  opacity: 0.5;
}

/* Column header: whole profile header cell is the drag handle (Field column has no handle class) */
.comparison-table thead .col-drag-handle {
  cursor: grab;
}

.comparison-table thead .col-drag-handle:active {
  cursor: grabbing;
}

.comparison-table thead th.col-profile-header-rich {
  vertical-align: top;
  padding-top: 14px;
  padding-bottom: 14px;
}

/* Row drag starts only from the field cell; cursor indicates draggable area */
.comparison-table tbody .row-drag-handle {
  cursor: grab;
}

.comparison-table tbody .row-drag-handle:active {
  cursor: grabbing;
}
</style>
